import React from 'react'
import { normalizedWarrantName } from '@finance-lab/utils/shared'

import { useTranslation } from 'react-i18next'
import {
    formatBigMoney,
    formatDateYYYYMMDD,
    formatMoney,
    formatPercentage,
} from '../../packages/utils/numberFormatters'
import InfoBlock from '../components/InfoBlock'
import { HasWarrantLabel } from '../../components/entities/IssueTypeLabels'

import { EnrichedWarrantEntity } from 'types/WarrantEntity'
import { WarrantStatus } from './WarrantDetails'
import { findSubscriptionPeriodCloseToDate, getAmountForPriceAtDate, hasSubPeriodPrelPrice } from '../../utils/warrants'
import { isFuture, isPast } from 'date-fns'
import { WarrantCommitChart } from '../../components/entities/WarrantCommitChart'
import { WarrantOutcomeChart } from '../../components/entities/WarrantOutcomeChart'
import { IssueWarrantSummary } from '../../components/entities/IssueWarrantSummary'

type WarrantSummaryComponent = React.FC<{
    hasCompanyLink?: boolean
    hasLink?: boolean
    isDetailsPage?: boolean
    isLoading: boolean
    isSlim?: boolean
    warrant: EnrichedWarrantEntity
}>

const getSize = (warrant: EnrichedWarrantEntity, locale: string) => {
    const { subscriptionPeriods, totalNumberOfWarrants } = warrant
    const { warrantSubPrice, prelSubPrice, subStartDate } = warrant.currentSubscriptionPeriod
    const showPrelPrice = hasSubPeriodPrelPrice(warrant.currentSubscriptionPeriod)
    const price = showPrelPrice ? prelSubPrice : warrantSubPrice
    const amount =
        subStartDate === null
            ? null
            : getAmountForPriceAtDate(
                  price,
                  totalNumberOfWarrants,
                  subscriptionPeriods,
                  new Date(subStartDate).toISOString(),
              )

    return (
        <>
            {formatBigMoney(amount, {
                currencySymbol: warrant.currency,
                decimals: 0,
                locale,
                tooltip: true,
            }) || '-'}
            {showPrelPrice && '*'}
        </>
    )
}

const getOngoingOrLatestSubPeriod = (warrant: EnrichedWarrantEntity) => {
    if (!warrant.subscriptionPeriods?.length) return null

    return (
        warrant.subscriptionPeriods.find((sp) => {
            if (sp.subStartDate == null || sp.subEndDate === null) return false

            return isPast(sp.subStartDate) && isFuture(sp.subEndDate)
        }) || warrant.subscriptionPeriods[warrant.subscriptionPeriods.length - 1]
    )
}

export const WarrantSummary: WarrantSummaryComponent = ({
    hasCompanyLink = true,
    isDetailsPage,
    hasLink,
    warrant,
    isLoading,
    isSlim,
}) => {
    const { i18n } = useTranslation()
    const locale = i18n.language

    if (!warrant) {
        return <InfoBlock title='Sammanfattning' loadingElement={false} />
    }

    const now = new Date().toISOString()
    const currentPeriod = findSubscriptionPeriodCloseToDate(warrant, now)
    const warrantSubPrice = currentPeriod?.warrantSubPrice

    const ongoingOrLatestSubPeriod = getOngoingOrLatestSubPeriod(warrant)
    const subPriceValue = warrantSubPrice ? (
        formatMoney(warrantSubPrice, {
            locale,
            currencySymbol: warrant.currency,
            minDecimals: 2,
            maxDecimals: 5,
        })
    ) : currentPeriod ? (
        <>
            <div>
                VWAP-rabatt:{' '}
                {formatPercentage(currentPeriod.vwapDiscount, {
                    locale,
                    minDecimals: 0,
                })}
            </div>
            <div>
                Min:{' '}
                {formatMoney(currentPeriod.priceMin, {
                    locale,
                    currencySymbol: warrant.currency,
                    minDecimals: 2,
                    maxDecimals: 5,
                }) || 'Kvotvärde'}
                {currentPeriod.priceMax && (
                    <>
                        , Max:{' '}
                        {formatMoney(currentPeriod.priceMax, {
                            locale,
                            currencySymbol: warrant.currency,
                            minDecimals: 2,
                            maxDecimals: 5,
                        })}
                    </>
                )}
            </div>
        </>
    ) : null
    const commitValue = ongoingOrLatestSubPeriod && (
        <WarrantCommitChart isLeftAlign subscriptionPeriod={ongoingOrLatestSubPeriod} />
    )

    const additionalRows = isSlim
        ? {}
        : {
              ...(warrant.unlisted
                  ? {}
                  : { 'Första handelsdag': formatDateYYYYMMDD(warrant.startTradeDate) || 'Handel ej inledd' }),
              ISIN: warrant.instrumentIsin || (warrant.unlisted ? 'Onoterat' : ''),
          }

    const relevantDateValue = !currentPeriod
        ? '-'
        : `${formatDateYYYYMMDD(currentPeriod.subStartDate)} - ${formatDateYYYYMMDD(currentPeriod.subEndDate)}`

    return (
        <IssueWarrantSummary
            hasLink={hasLink}
            isLoading={isLoading}
            isSlim={isSlim}
            label={<HasWarrantLabel warrant={warrant} />}
            status={<WarrantStatus warrant={warrant} details />}
            name={hasCompanyLink ? warrant.companyName : normalizedWarrantName(warrant)}
            companyCountryCode={warrant.companyCountryCode}
            typeLabel={'Teckningsoption'}
            isDetailsPage={!!isDetailsPage}
            companyId={warrant.companyId}
            sizeLabel={'Belopp'}
            sizeValue={getSize(warrant, locale)}
            subPriceValue={subPriceValue}
            relevantDateLabel={'Sista teckningsdag'}
            relevantDateValue={relevantDateValue}
            commitValue={commitValue}
            outcomeValue={ongoingOrLatestSubPeriod && <WarrantOutcomeChart isLeftAlign warrant={warrant} />}
            detailLink={`/warrants/${warrant.id}`}
            noncashConsiderationValue={null}
            additionalRows={additionalRows}
        />
    )
}
